import React, { useRef, useState } from 'react';
import {
  Tag,
  Button,
  Input,
  Typography,
  Space,
  Collapse,
  Divider,
  Modal,
  Form,
  Select,
  InputNumber,
  Tooltip,
  Popconfirm,
  App,
} from 'antd';
import {
  LinkOutlined,
  DisconnectOutlined,
  ExperimentOutlined,
  InboxOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  useGetProductsWithStockQuery,
  useLinkProductToInventoryMutation,
  useUnlinkProductFromInventoryMutation,
} from '../../services/productsApi';
import {
  useGetInventoryCategoriesQuery,
  useCreateInventoryCategoryMutation,
} from '../../services/inventoryApi';
import { useCurrency } from '../../hooks/useCurrency';
import { LinkProductInventoryRequest } from '../../types/products';

const { Text } = Typography;
const { Option } = Select;

const UNIT_OPTIONS = [
  { label: 'Kilogramos (kg)', value: 'kg' },
  { label: 'Litros (L)', value: 'L' },
  { label: 'Piezas (pz)', value: 'pz' },
  { label: 'Cajas', value: 'caja' },
  { label: 'Gramos (g)', value: 'g' },
  { label: 'Mililitros (ml)', value: 'ml' },
];

interface ProductInventorySectionProps {
  productId?: string; // undefined when creating new product
  isMobile?: boolean;
}

const ProductInventorySection: React.FC<ProductInventorySectionProps> = ({
  productId,
  isMobile = false,
}) => {
  const { message: messageApi } = App.useApp();
  const { currencySymbol } = useCurrency();

  const { data: productsWithStock = [] } = useGetProductsWithStockQuery();
  const { data: inventoryCategories = [] } = useGetInventoryCategoriesQuery();
  const [linkProduct, { isLoading: linking }] = useLinkProductToInventoryMutation();
  const [unlinkProduct, { isLoading: unlinking }] = useUnlinkProductFromInventoryMutation();
  const [createCategory, { isLoading: creatingCategory }] = useCreateInventoryCategoryMutation();

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkForm] = Form.useForm();
  const [newCategoryName, setNewCategoryName] = useState('');
  const categoryInputRef = useRef<any>(null);

  // Find current product's stock info
  const stockInfo = productId
    ? productsWithStock.find((p) => p.productId === productId)
    : undefined;

  const linkType = stockInfo?.inventoryLinkType || 'none';

  // =========================================================
  // Inline category creation
  // =========================================================

  const handleCreateCategory = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    try {
      const result = await createCategory({
        name: newCategoryName.trim(),
        isActive: true,
      }).unwrap();
      setNewCategoryName('');
      linkForm.setFieldsValue({ inventoryCategoryId: result.id });
      messageApi.success('Categoria creada');
    } catch (error: any) {
      messageApi.error(error?.data?.message || 'Error al crear categoria');
    }
  };

  // =========================================================
  // Link handler
  // =========================================================

  const handleOpenLinkModal = () => {
    linkForm.resetFields();
    linkForm.setFieldsValue({
      currentStock: 0,
      minimumStock: 0,
      costPerUnit: 0,
      conversionFactor: 1,
    });
    setLinkModalOpen(true);
  };

  const handleLink = async () => {
    if (!productId) return;
    try {
      const values = await linkForm.validateFields();
      const data: LinkProductInventoryRequest = {
        inventoryCategoryId: values.inventoryCategoryId,
        unit: values.unit,
        currentStock: values.currentStock,
        minimumStock: values.minimumStock,
        costPerUnit: values.costPerUnit,
        conversionFactor: values.conversionFactor,
      };
      await linkProduct({ id: productId, data }).unwrap();
      messageApi.success('Producto vinculado al inventario');
      setLinkModalOpen(false);
      linkForm.resetFields();
    } catch (error: any) {
      if (error?.errorFields) return;
      messageApi.error(error?.data?.message || 'Error al vincular');
    }
  };

  // =========================================================
  // Unlink handler
  // =========================================================

  const handleUnlink = async () => {
    if (!productId) return;
    try {
      await unlinkProduct(productId).unwrap();
      messageApi.success('Producto desvinculado del inventario');
    } catch (error: any) {
      messageApi.error(error?.data?.message || 'Error al desvincular');
    }
  };

  // =========================================================
  // Render status
  // =========================================================

  const renderStatus = () => {
    if (!productId) {
      return (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Guarde el producto primero. Luego podra vincularlo al inventario desde aqui o desde la tab Stock.
        </Text>
      );
    }

    switch (linkType) {
      case 'direct':
        return (
          <div>
            <div style={{ marginBottom: 8 }}>
              <Tag color="blue" icon={<InboxOutlined />}>Insumo directo</Tag>
              <Tag color={stockInfo?.isLowStock ? 'red' : 'green'}>
                {stockInfo?.currentStock} {stockInfo?.unit}
              </Tag>
            </div>
            {stockInfo?.conversionFactor !== undefined && stockInfo.conversionFactor !== 1 && (
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Factor: {stockInfo.conversionFactor} {stockInfo.unit} por venta
                </Text>
              </div>
            )}
            <Popconfirm
              title="Desvincular del inventario"
              description="El insumo seguira existiendo en inventario pero ya no se descontara automaticamente."
              onConfirm={handleUnlink}
              okText="Desvincular"
              cancelText="Cancelar"
              okButtonProps={{ danger: true, loading: unlinking }}
            >
              <Button size="small" danger icon={<DisconnectOutlined />}>
                Desvincular
              </Button>
            </Popconfirm>
          </div>
        );

      case 'recipe':
        return (
          <div>
            <div style={{ marginBottom: 8 }}>
              <Tag color="purple" icon={<ExperimentOutlined />}>Receta</Tag>
              <Tooltip title={stockInfo?.hasSufficientStock ? 'Stock suficiente' : 'Stock insuficiente para ingredientes'}>
                <Tag color={stockInfo?.hasSufficientStock ? 'green' : 'orange'}>
                  {stockInfo?.ingredientCount} ingredientes
                </Tag>
              </Tooltip>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Administre la receta desde Inventario &gt; Recetas
            </Text>
          </div>
        );

      default:
        return (
          <div>
            <div style={{ marginBottom: 8 }}>
              <Tag>Sin inventario</Tag>
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              No se descuenta stock al vender este producto.
            </Text>
            <Button
              size="small"
              type="primary"
              ghost
              icon={<LinkOutlined />}
              onClick={handleOpenLinkModal}
            >
              Vincular como insumo
            </Button>
          </div>
        );
    }
  };

  // =========================================================
  // Render
  // =========================================================

  return (
    <>
      <Collapse
        size="small"
        items={[
          {
            key: 'inventory',
            label: (
              <Space size="small">
                <InboxOutlined />
                <span>Inventario</span>
                {productId && linkType !== 'none' && (
                  <Tag
                    color={linkType === 'direct' ? 'blue' : 'purple'}
                    style={{ marginLeft: 4 }}
                  >
                    {linkType === 'direct' ? 'Insumo' : 'Receta'}
                  </Tag>
                )}
              </Space>
            ),
            children: renderStatus(),
          },
        ]}
        style={{ marginTop: 8, marginBottom: 8 }}
      />

      {/* Link Modal */}
      <Modal
        title="Vincular como insumo directo"
        open={linkModalOpen}
        onCancel={() => {
          setLinkModalOpen(false);
          linkForm.resetFields();
        }}
        onOk={handleLink}
        confirmLoading={linking}
        okText="Vincular"
        cancelText="Cancelar"
        width={isMobile ? '98%' : 500}
        destroyOnClose
      >
        <Form form={linkForm} layout="vertical">
          <Form.Item
            name="inventoryCategoryId"
            label="Categoria de inventario"
            rules={[{ required: true, message: 'Seleccione una categoria' }]}
          >
            <Select
              placeholder="Seleccione una categoria"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px' }}>
                    <Input
                      placeholder="Nueva categoria"
                      ref={categoryInputRef}
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      size="small"
                    />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={handleCreateCategory}
                      loading={creatingCategory}
                      size="small"
                    >
                      Crear
                    </Button>
                  </Space>
                </>
              )}
            >
              {inventoryCategories
                .filter((c) => c.isActive)
                .map((c) => (
                  <Option key={c.id} value={c.id}>{c.name}</Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="unit"
            label="Unidad de medida"
            rules={[{ required: true, message: 'Seleccione la unidad' }]}
          >
            <Select placeholder="Seleccione la unidad">
              {UNIT_OPTIONS.map((u) => (
                <Option key={u.value} value={u.value}>{u.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item
              name="currentStock"
              label="Stock inicial"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="minimumStock"
              label="Stock minimo"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item
              name="costPerUnit"
              label={`Costo por unidad (${currencySymbol})`}
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="conversionFactor"
              label="Factor de conversion"
              style={{ flex: 1 }}
              tooltip="Cuantas unidades de inventario se descuentan por cada producto vendido. Ej: 1 cerveza = 1 pz, 1 cerveza de caja de 24 = 0.0417"
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <InputNumber min={0.0001} precision={4} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ProductInventorySection;
